import React from 'react'
import { Link } from '@tanstack/react-router'
import { Award, ShieldCheck, CheckCircle2, Sparkles, FileText, ArrowLeft, Info } from 'lucide-react'
import { StorefrontLayout } from '../layouts/StorefrontLayout'
import { useSiteConfig } from '../context/SiteConfigContext'
import { isGstEnabled } from '../lib/companyGst'

export const QualityGuidePage: React.FC = () => {
  const { config } = useSiteConfig()
  const gstOn = isGstEnabled(config)

  return (
    <StorefrontLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to store
        </Link>

        <article className="glass-luxury-card p-6 sm:p-10 space-y-8">
          <header className="flex flex-col sm:flex-row gap-4 border-b border-[var(--border)] pb-6">
            <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-[var(--gold-main)] to-[var(--gold-light)] p-0.5 shrink-0">
              <div className="w-full h-full rounded-full bg-[var(--bg-main)] flex items-center justify-center text-[var(--gold-main)]">
                <Award className="w-7 h-7" />
              </div>
            </div>
            <div>
              <span className="badge badge-hallmark mb-2">{config.brand_name} Quality Standard</span>
              <h1 className="text-2xl font-title font-bold">Gold Plated Covering &amp; Warranty Guide</h1>
              <p className="text-sm text-[var(--text-muted)] mt-2">
                Premium gold plated fashion jewellery with anti-tarnish coating &amp; 1-year colour replacement guarantee.
              </p>
            </div>
          </header>

          <div className="flex items-start gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-surface-2)] p-4 text-xs text-[var(--text-muted)]">
            <Info className="w-4 h-4 text-[var(--gold-main)] shrink-0 mt-0.5" />
            <p>
              All {config.brand_name} pieces are <strong>gold plated covering fashion jewellery</strong> — not solid gold,
              BIS hallmarked gold, or 1-gram gold. We specialise in beautiful gold-finish plating at accessible prices.
            </p>
          </div>

          <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                icon: Award,
                title: 'Premium Gold Plating',
                text: 'Multi-layer gold plated covering for a rich, lustrous finish that mimics real gold jewellery.',
              },
              {
                icon: ShieldCheck,
                title: '1-Year Colour Warranty',
                text: 'Free replacement if fading or discoloration occurs within 12 months of purchase.',
              },
              {
                icon: Sparkles,
                title: 'Anti-Tarnish Coating',
                text: 'Protective seal against oxidation, sweat and daily wear for longer-lasting shine.',
              },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="p-4 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)]">
                <div className="flex items-center gap-2 font-bold text-sm mb-2">
                  <Icon className="w-4 h-4 text-[var(--gold-main)]" />
                  {title}
                </div>
                <p className="text-xs text-[var(--text-muted)] leading-relaxed">{text}</p>
              </div>
            ))}
          </section>

          <section className="rounded-xl border border-[var(--border)] p-5 bg-[var(--bg-surface-2)] space-y-2">
            <h2 className="font-bold text-sm text-[var(--text-primary)]">Care tips for gold plated jewellery</h2>
            <ul className="text-xs text-[var(--text-muted)] space-y-1.5 list-disc list-inside leading-relaxed">
              <li>Keep away from water, perfume and harsh chemicals</li>
              <li>Wipe with a soft dry cloth after each wear</li>
              <li>Store in the pouch provided, away from direct sunlight</li>
              <li>Avoid sleeping or exercising while wearing plated pieces</li>
            </ul>
          </section>

          {gstOn && (
            <section className="rounded-xl border border-[var(--border)] p-5 bg-[var(--bg-surface-2)] space-y-2">
              <div className="flex items-center gap-2 font-bold text-sm text-[var(--gold-main)]">
                <FileText className="w-4 h-4" />
                GST Tax Invoicing (HSN 7113)
              </div>
              <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                Every order includes an itemized invoice with <strong>3% CGST</strong> + <strong>3% SGST</strong> and warranty reference.
              </p>
            </section>
          )}

          <footer className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-[var(--border)]">
            <p className="text-xs text-emerald-600 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" />
              Insured shipping across India
            </p>
            <Link to="/" className="btn-gold text-xs py-2.5 px-6">Shop collection</Link>
          </footer>
        </article>
      </div>
    </StorefrontLayout>
  )
}
